import { AUDIO_CONFIG } from '../config/constants';

/**
 * Audio Service
 * Manages a singleton AudioContext and provides utility methods
 */

class AudioService {
  constructor() {
    this.audioContext = null;
    this.isInitialized = false;
  }

  /**
   * Get or create the AudioContext instance
   * @returns {AudioContext} The audio context
   */
  getContext() {
    if (!this.audioContext) {
      try {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.isInitialized = true;
      } catch (error) {
        console.error('Failed to create AudioContext:', error);
        throw new Error('Web Audio API is not supported in this browser');
      }
    }

    // Resume context if suspended (browser autoplay policy)
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch(error => {
        console.error('Failed to resume AudioContext:', error);
      });
    }

    return this.audioContext;
  }

  /**
   * Close the AudioContext
   * @returns {Promise<void>}
   */
  async closeContext() {
    if (this.audioContext) {
      try {
        await this.audioContext.close();
        this.audioContext = null;
        this.isInitialized = false;
      } catch (error) {
        console.error('Error closing AudioContext:', error);
      }
    }
  }

  /**
   * Apply smooth volume change to a gain node
   * @param {GainNode} gainNode - The gain node to modify
   * @param {number} targetVolume - Target volume (0-1)
   * @param {number} duration - Transition duration in seconds
   */
  setVolume(gainNode, targetVolume, duration = AUDIO_CONFIG.VOLUME_TRANSITION_TIME) {
    if (!gainNode) return;

    try {
      const currentTime = gainNode.context.currentTime;
      gainNode.gain.cancelScheduledValues(currentTime);
      gainNode.gain.setValueAtTime(gainNode.gain.value, currentTime);
      
      // Use exponential ramp for more natural volume changes
      // Clamp to minimum value to avoid errors with exponentialRamp
      const safeVolume = Math.max(targetVolume, AUDIO_CONFIG.MIN_GAIN_VALUE);
      gainNode.gain.exponentialRampToValueAtTime(safeVolume, currentTime + duration);
    } catch (error) {
      console.error('Error setting volume:', error);
      // Fallback to direct set
      gainNode.gain.value = targetVolume;
    }
  }

  /**
   * Normalize volume from 0-100 range to 0-1 range
   * @param {number} volume - Volume in 0-100 range
   * @returns {number} Normalized volume in 0-1 range
   */
  normalizeVolume(volume) {
    return volume / AUDIO_CONFIG.VOLUME_NORMALIZE_FACTOR;
  }

  /**
   * Update pan width smoothly
   * @param {Object} panNodes - Object with left and right panner nodes
   * @param {number} panWidth - New pan width (0-1)
   * @param {number} duration - Transition duration in seconds
   */
  setPanWidth(panNodes, panWidth, duration = 0.05) {
    if (!panNodes || !panNodes.left || !panNodes.right) return;

    try {
      const currentTime = panNodes.left.context.currentTime;
      const transitionTime = currentTime + duration;
      
      panNodes.left.pan.linearRampToValueAtTime(-panWidth, transitionTime);
      panNodes.right.pan.linearRampToValueAtTime(panWidth, transitionTime);
    } catch (error) {
      console.error('Error setting pan width:', error);
      // Fallback to direct set
      panNodes.left.pan.value = -panWidth;
      panNodes.right.pan.value = panWidth;
    }
  }

  /**
   * Check if Web Audio API is supported
   * @returns {boolean}
   */
  isSupported() {
    return !!(window.AudioContext || window.webkitAudioContext);
  }
}

// Export singleton instance
export default new AudioService();
